import { z } from 'zod';
import { ShareRole, TripStatus } from './enums';
import { name, uuid } from './fields';

/**
 * Trips — a specific occasion, a working copy assembled from Master Lists
 * (data-model.md §8). REST endpoints land in T14; DTO shapes pinned now.
 *
 * Code invariant (data-model.md §8): `closedAt` is non-null IFF
 * `status === 'closed'`. Enforced in the API (close sets both, reopen nulls
 * `closedAt`) — the schema only makes it expressible.
 */

/** Bounds for the per-trip list-card column width (px). Narrower ⇒ more columns. */
export const LIST_WIDTH_MIN = 320;
export const LIST_WIDTH_MAX = 1000;

/** Target column width (px) for the trip's masonry of list cards. */
const listWidth = () => z.number().int().min(LIST_WIDTH_MIN).max(LIST_WIDTH_MAX);

/** Full Trip resource (response shape). */
const BaseTrip = z.object({
  id: uuid(),
  ownerId: uuid(),
  name: name(),
  listWidth: listWidth(),
  /**
   * Live collaboration is on for this trip: everyone who has the feature
   * enabled in their account streams edits and presence instead of polling.
   */
  liveCollaboration: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const Trip = z.discriminatedUnion('status', [
  BaseTrip.extend({
    status: z.literal('active'),
    closedAt: z.null(),
  }),
  BaseTrip.extend({
    status: z.literal('closed'),
    closedAt: z.string().datetime(),
  }),
]);
export type Trip = z.infer<typeof Trip>;

/** POST body — server sets `ownerId`, `status='active'`, timestamps. */
export const CreateTripInput = z.object({
  name: name(),
});
export type CreateTripInput = z.infer<typeof CreateTripInput>;

/**
 * PATCH body — rename only. Lifecycle (close/reopen) is its own action that
 * also manages `closedAt`, not a free `status` write, so `status` is NOT here.
 */
export const UpdateTripInput = z
  .object({
    name: name(),
    listWidth: listWidth(),
    liveCollaboration: z.boolean(),
  })
  .partial();
export type UpdateTripInput = z.infer<typeof UpdateTripInput>;

/** Optional list filter for `GET /api/trips`. */
export const ListTripsQuery = z
  .object({
    status: TripStatus.optional(),
  })
  .partial();
export type ListTripsQuery = z.infer<typeof ListTripsQuery>;

/**
 * Pull-in body (T15/T18). The caller can select direct Master Lists, Activities,
 * or both. The API resolves Activities to their bundled lists, dedupes lists by
 * id while preserving selection order, then appends working-copy trip items.
 */
export const PullIntoTripInput = z.object({
  masterListIds: z.array(uuid()).max(100).optional().default([]),
  activityIds: z.array(uuid()).max(100).optional().default([]),
});
export type PullIntoTripInput = z.infer<typeof PullIntoTripInput>;

/* -------------------------------------------------------------------------- */
/* Trip collaborators — wider helpers invited at the trip level (§9, PRD §12)  */
/* -------------------------------------------------------------------------- */

/** Full collaborator resource (response shape). */
export const TripCollaborator = z.object({
  id: uuid(),
  tripId: uuid(),
  userId: uuid(),
  role: ShareRole,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type TripCollaborator = z.infer<typeof TripCollaborator>;

/** Collaborator response enriched with the invited user's public identity. */
export const TripCollaboratorWithUser = TripCollaborator.extend({
  email: z.string().email(),
  displayName: z.string().nullable(),
});
export type TripCollaboratorWithUser = z.infer<typeof TripCollaboratorWithUser>;

/** Anyone on the trip — the owner plus every collaborator — as an assignee. */
export const TripMember = z.object({
  id: uuid(),
  email: z.string().email(),
  displayName: z.string().nullable(),
  role: z.enum(['owner', 'viewer', 'editor']),
});
export type TripMember = z.infer<typeof TripMember>;

/** Invite body — grantee resolved from email via Clerk lookup (T30). */
export const CreateTripCollaboratorInput = z.object({
  email: z.string().trim().email(),
  role: ShareRole,
});
export type CreateTripCollaboratorInput = z.infer<typeof CreateTripCollaboratorInput>;

/** Role-change body. */
export const UpdateTripCollaboratorInput = z.object({
  role: ShareRole,
});
export type UpdateTripCollaboratorInput = z.infer<typeof UpdateTripCollaboratorInput>;

/* -------------------------------------------------------------------------- */
/* Trip invites — pending, email-keyed invitations (no account required yet).  */
/* An invite grants no access until the invitee accepts it.                    */
/* -------------------------------------------------------------------------- */

/** A pending invite as the inviting owner sees it (Manage → Collaborators). */
export const TripInvite = z.object({
  id: uuid(),
  tripId: uuid(),
  email: z.string().email(),
  role: ShareRole,
  invitedBy: uuid(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type TripInvite = z.infer<typeof TripInvite>;

/** Invite body — email need not belong to an existing account. */
export const CreateTripInviteInput = z.object({
  email: z.string().trim().email(),
  role: ShareRole,
});
export type CreateTripInviteInput = z.infer<typeof CreateTripInviteInput>;

/** A pending invite as the INVITEE sees it on their home screen (enriched). */
export const IncomingTripInvite = z.object({
  id: uuid(),
  tripId: uuid(),
  tripName: name(),
  role: ShareRole,
  invitedByName: z.string().nullable(),
  invitedByEmail: z.string().email(),
  createdAt: z.string().datetime(),
});
export type IncomingTripInvite = z.infer<typeof IncomingTripInvite>;
